import React, { useState, useEffect } from 'react';
import { 
  Descriptions, 
  Table, 
  Card, 
  Tag, 
  Button, 
  Space, 
  message,
  Empty,
  Image
} from 'antd';
import EllipsisCell from '../../../../components/common/EllipsisCell';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import * as orderService from '../../../../services/order/orderService';
import { ORDER_STATUS_OPTIONS, CHANNEL_OPTIONS, SHORTAGE_REASON_OPTIONS } from '../../../../constants/order';

/**
 *缺货订单详情页
 *路: /order/shortage-order/:orderNo
 */
const ShortageOrderDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderNo } = useParams();
  const [loading, setLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);

  // 获取缺货订单详情
  const fetchOrderDetail = async () => {
    if (!orderNo) return;
    
    setLoading(true);
    try {
      const response = await orderService.getShortageOrderByOrderNo(orderNo);
      
      if (response.success) {
        setOrderDetail(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询缺货订单详情失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [orderNo]);

  const shortageInfo = orderDetail || {};
  const shortageDetails = shortageInfo.shortageDetails || [];

  //基本信息描述项
  const basicInfoItems = [
    {
      key: 'orderNo',
      label: t('order.shortageOrder.detail.basic.orderNo'),
      children: shortageInfo.orderNo,
      span: 2
    },
    {
      key: 'channel',
      label: t('order.shortageOrder.detail.basic.channel'),
      children: (() => {
        const option = CHANNEL_OPTIONS.find(opt => opt.value === shortageInfo.channel);
        return option ? t(option.label) : (shortageInfo.channel || '-');
      })(),
      span: 1
    },
    {
      key: 'orderStatus',
      label: t('order.shortageOrder.detail.basic.orderStatus'),
      children: (
        <Tag color={ORDER_STATUS_OPTIONS.find(opt => opt.value === shortageInfo.orderStatus)?.color}>
          {t(ORDER_STATUS_OPTIONS.find(opt => opt.value === shortageInfo.orderStatus)?.label || shortageInfo.orderStatus)}
        </Tag>
      ),
      span: 1
    },
    {
      key: 'buyerName',
      label: t('order.shortageOrder.detail.basic.buyerName'),
      children: shortageInfo.buyerName || '-',
      span: 1
    },
    {
      key: 'buyerPhone',
      label: t('order.shortageOrder.detail.basic.buyerPhone'),
      children: shortageInfo.buyerPhone || '-',
      span: 1
    },
    {
      key: 'skuQty',
      label: t('order.shortageOrder.detail.basic.skuQty'),
      children: shortageInfo.skuQty || 0,
      span: 1
    },
    {
      key: 'totalMu',
      label: t('order.shortageOrder.detail.basic.totalMu'),
      children: shortageInfo.totalMu || 0,
      span: 1
    },
    {
      key: 'shortageSkuCount',
      label: t('order.shortageOrder.detail.basic.shortageSkuCount'),
      children: (
        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
          {shortageInfo.shortageSkuCount || 0}
        </span>
      ),
      span: 1
    },
    {
      key: 'shortageItemCount',
      label: t('order.shortageOrder.detail.basic.shortageItemCount'),
      children: (
        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
          {shortageInfo.shortageItemCount || 0}
        </span>
      ),
      span: 1
    },
    {
      key: 'replacedSkuCount',
      label: t('order.shortageOrder.detail.basic.replacedSkuCount'),
      children: shortageInfo.replacedSkuCount || 0,
      span: 1
    },
    {
      key: 'unreplacedSkuCount',
      label: t('order.shortageOrder.detail.basic.unreplacedSkuCount'),
      children: (
        <span style={{ color: shortageInfo.unreplacedSkuCount > 0 ? '#ff4d4f' : '#52c41a', fontWeight: '500' }}>
          {shortageInfo.unreplacedSkuCount || 0}
        </span>
      ),
      span: 1
    },
    {
      key: 'pickerName',
      label: t('order.shortageOrder.detail.basic.pickerName'),
      children: shortageInfo.pickerName || '-',
      span: 1
    },
    {
      key: 'createTime',
      label: t('order.shortageOrder.detail.basic.createTime'),
      children: shortageInfo.createTime ? dayjs(shortageInfo.createTime).format('DD-MM-YYYY HH:mm:ss') : '-',
      span: 2
    }
  ];

  //缺货明细表格列
  const shortageColumns = [
    {
      title: t('order.shortageOrder.detail.shortage.lineNo'),
      dataIndex: 'lineNo',
      key: 'lineNo',
      width: 80,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('order.shortageOrder.detail.shortage.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 150,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('order.shortageOrder.detail.shortage.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('order.shortageOrder.detail.shortage.isWeighted'),
      dataIndex: 'isWeighted',
      key: 'isWeighted',
      width: 80,
      render: (isWeighted) => (
        <Tag color={isWeighted ? 'blue' : 'default'}>
          {isWeighted ? t('common.yes') : t('common.no')}
        </Tag>
      )
    },
    {
      title: t('order.shortageOrder.detail.shortage.planQty'),
      dataIndex: 'planQty',
      key: 'planQty',
      width: 100,
      align: 'right',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('order.shortageOrder.detail.shortage.actualPickedQty'),
      dataIndex: 'actualPickedQty',
      key: 'actualPickedQty',
      width: 100,
      align: 'right',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('order.shortageOrder.detail.shortage.shortageQty'),
      dataIndex: 'shortageQty',
      key: 'shortageQty',
      width: 100,
      align: 'right',
      render: (qty) => (
        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
          <EllipsisCell>{qty}</EllipsisCell>
        </span>
      )
    },
    {
      title: t('order.shortageOrder.detail.shortage.unitPrice'),
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      align: 'right',
      render: (price) => <EllipsisCell>{`฿${price?.toFixed(2) || '0.00'}`}</EllipsisCell>
    },
    {
      title: t('order.shortageOrder.detail.shortage.shortageAmount'),
      dataIndex: 'shortageAmount',
      key: 'shortageAmount',
      width: 100,
      align: 'right',
      render: (amount) => (
        <span style={{ color: '#ff4d4f', fontWeight: '500' }}>
          <EllipsisCell>฿{amount?.toFixed(2) || '0.00'}</EllipsisCell>
        </span>
      )
    },
    {
      title: t('order.shortageOrder.detail.shortage.shortageReason'),
      dataIndex: 'shortageReason',
      key: 'shortageReason',
      width: 120,
      render: (reason) => {
        const option = SHORTAGE_REASON_OPTIONS.find(opt => opt.value === reason);
        return <EllipsisCell>{t(option?.label || reason)}</EllipsisCell>;
      }
    },
    {
      title: t('order.shortageOrder.detail.shortage.isReplaced'),
      dataIndex: 'isReplaced',
      key: 'isReplaced',
      width: 80,
      render: (isReplaced) => (
        <Tag color={isReplaced ? 'green' : 'default'}>
          {isReplaced ? t('common.yes') : t('common.no')}
        </Tag>
      )
    },
    {
      title: t('order.shortageOrder.detail.shortage.substituteItemCode'),
      dataIndex: 'substituteItemCode',
      key: 'substituteItemCode',
      width: 150,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('order.shortageOrder.detail.shortage.substituteItemName'),
      dataIndex: 'substituteItemName',
      key: 'substituteItemName',
      width: 200,
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: 'Substitute Image',
      dataIndex: 'substituteImageUrl',
      key: 'substituteImageUrl',
      width: 80,
      render: (url) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
  ];

  return (
    <div>
      {/* 页面标题和返回按钮 */}
      <div style={{ 
        marginBottom: '24px', 
        padding: '16px 0',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#1d1d1d' 
        }}>
          {t('order.shortageOrder.detail.title')} - {orderNo}
        </h2>
        <Button onClick={() => navigate(-1)}>
          {t('common.back')}
        </Button>
      </div>
      
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/*基本信息 */}
        <Card title={t('order.shortageOrder.detail.basicInfo')}>
          <Descriptions 
            column={4} 
            bordered
            size="middle"
            items={basicInfoItems}
          />
        </Card>
        
        {/*缺明细 */}
        <Card title={t('order.shortageOrder.detail.shortage.title')}>
          <Table
            columns={shortageColumns}
            dataSource={shortageDetails}
            pagination={false}
            rowKey="lineNo"
            scroll={{ x: 1400 }}
            locale={{
              emptyText: <Empty description={t('common.message.noData') || '暂无数据'} />
            }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default ShortageOrderDetail;